import type { McpFirmProfile, McpXpmPrefill } from '@/types/mcp'

export const FIRM_PROFILE_LIMITS = {
  firmName: 120,
  financialYear: 120,
  size: 120,
  jobNaming: 1000,
  teams: 30,
  teamName: 120,
  teamLead: 120,
  keyPeople: 30,
  keyPersonName: 120,
  keyPersonRole: 120,
  lines: 20,
  note: 500,
  objectives: 10,
  objective: 200,
  initiatives: 10,
  initiative: 300,
} as const

export const maxChars = (max: number) => ({ max, message: `Keep it to ${max} characters.` })

export type FirmProfileFormValues = {
  firmName: string
  financialYear: string
  size: string
  teams: { name: string; lead: string }[]
  keyPeople: { name: string; role: string }[]
  jobNaming: string
  workflowNotes: string[]
  reportingNotes: string[]
  objectives: { objective: string; initiatives: string[] }[]
  notes: string[]
}

const trim = (value: string | undefined | null) => (value ?? '').trim()
const lines = (values: (string | undefined | null)[] | undefined) => (values ?? []).map(trim).filter(Boolean)

/** An empty firm name starts from XPM's; everything else is what was saved. */
export function toFormValues(profile: McpFirmProfile, prefill: McpXpmPrefill): FirmProfileFormValues {
  return {
    firmName: profile.firmName || prefill.firmName,
    financialYear: profile.financialYear ?? '',
    size: profile.size ?? '',
    teams: profile.people?.teams ?? [],
    keyPeople: profile.people?.keyPeople ?? [],
    jobNaming: profile.jobNaming ?? '',
    workflowNotes: profile.workflowNotes ?? [],
    reportingNotes: profile.reportingNotes ?? [],
    objectives: profile.objectives ?? [],
    notes: profile.notes ?? [],
  }
}

/** The whole profile, as PUT replaces it. */
export function toProfile(values: FirmProfileFormValues): McpFirmProfile {
  return {
    firmName: trim(values.firmName),
    financialYear: trim(values.financialYear),
    size: trim(values.size),
    people: {
      teams: (values.teams ?? [])
        .map((team) => ({ name: trim(team?.name), lead: trim(team?.lead) }))
        .filter((team) => team.name),
      keyPeople: (values.keyPeople ?? [])
        .map((person) => ({ name: trim(person?.name), role: trim(person?.role) }))
        .filter((person) => person.name),
    },
    jobNaming: trim(values.jobNaming),
    workflowNotes: lines(values.workflowNotes),
    reportingNotes: lines(values.reportingNotes),
    objectives: (values.objectives ?? [])
      .map((item) => ({ objective: trim(item?.objective), initiatives: lines(item?.initiatives) }))
      .filter((item) => item.objective),
    notes: lines(values.notes),
  }
}
